import React from 'react'
import {useTranslation} from 'react-i18next'
import {
    Breakpoints,
    FilterTag,
    MultiSelect,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'

import {MultiSelectAdvanced, Result} from './MultiSelectAdvanced'
import {State} from '../types/state'

const FiltersSelection = styled.div`
    margin-bottom: ${(props) => props.theme.size.static370};
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.size.static300};

    @media (min-width: ${Breakpoints.b960}px) {
        flex-direction: row;
        gap: ${(props) => props.theme.size.static800};
    }
`

const FilterWrapper = styled.div`
    flex-grow: 1;
`

const ActiveFilters = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${(props) => props.theme.size.static200};
    margin-bottom: ${(props) => props.theme.size.static400};
    margin-top: ${(props) => props.theme.size.static400};
`

interface FiltersProps {
    provinces: string[]
    setState: React.Dispatch<React.SetStateAction<State>>
    state: State
}

export const Filters: React.FunctionComponent<FiltersProps> = (props) => {
    const {provinces, setState, state} = props
    const {t} = useTranslation('fa-posizioni-aperte')
    const provinceFilterRef = React.useRef<HTMLDivElement>(null)
    const provinceFilterLabelId = 'province-filter-label'
    const provinceFilterListboxId = 'province-filter-listbox'

    let resultsCategories = {} as Result

    state.categories.forEach((category) => {
        category.categories.forEach(({id}) => {
            resultsCategories = {
                ...resultsCategories,
                [id]: state.jobAdvertisements.filter((jobAdvertisement) =>
                    state.selectedProvinces.length
                        ? jobAdvertisement.category_id === id &&
                          state.selectedProvinces.includes(
                              jobAdvertisement.province
                          )
                        : jobAdvertisement.category_id === id
                ).length,
            }
        })
    })

    const onProvinceClick = (optionKey: string) => {
        const newValue = [...state.selectedProvinces]

        if (state.selectedProvinces?.includes(optionKey as never)) {
            newValue.splice(
                state.selectedProvinces.indexOf(optionKey as never),
                1
            )
        } else {
            newValue.push(optionKey as never)
        }

        setState({
            ...state,
            selectedProvinces: newValue,
        })
    }

    const onCategoryClick = (optionKey: number) => {
        const newValue = [...state.selectedCategories]

        if (state.selectedCategories?.includes(optionKey as never)) {
            newValue.splice(
                state.selectedCategories.indexOf(optionKey as never),
                1
            )
        } else {
            newValue.push(optionKey as never)
        }

        setState({
            ...state,
            selectedCategories: newValue,
        })
    }

    React.useEffect(() => {
        const listbox = provinceFilterRef.current?.querySelector(
            `#${provinceFilterListboxId}`
        )

        listbox?.setAttribute('aria-labelledby', provinceFilterLabelId)
    }, [])

    return (
        <>
            <FiltersSelection>
                <FilterWrapper ref={provinceFilterRef}>
                    <MultiSelect
                        aria-label={t('jobAdvertisementsList.filterByProvince')}
                        controlsId={provinceFilterListboxId}
                        label={t('jobAdvertisementsList.filterByProvince')}
                        labelId={provinceFilterLabelId}
                        name="province"
                        onOptionClick={onProvinceClick}
                        options={provinces.reduce(
                            (acc, item) => ({
                                ...acc,
                                [item]: item,
                            }),
                            {}
                        )}
                        results={provinces.map(
                            (province) =>
                                state.jobAdvertisements.filter(
                                    (jobAdvertisement) => {
                                        if (state.selectedCategories.length) {
                                            return (
                                                state.selectedCategories.includes(
                                                    jobAdvertisement.category_id
                                                ) &&
                                                jobAdvertisement.province ===
                                                    province
                                            )
                                        }

                                        return (
                                            jobAdvertisement.province ===
                                            province
                                        )
                                    }
                                ).length
                        )}
                        value={state.selectedProvinces}
                    />
                </FilterWrapper>

                <FilterWrapper>
                    <MultiSelectAdvanced
                        categorization={true}
                        controlsId="job-filter-options"
                        label={t('jobAdvertisementsList.filterByJob')}
                        labelId="job-filter-label"
                        name="job"
                        onOptionClick={(value) =>
                            onCategoryClick(parseInt(value, 10))
                        }
                        options={state.categories}
                        results={resultsCategories}
                        value={state.selectedCategories}
                    />
                </FilterWrapper>
            </FiltersSelection>

            {(state.selectedProvinces.length > 0 ||
                state.selectedCategories.length > 0) && (
                <ActiveFilters>
                    {state.selectedProvinces.map((province, index) => (
                        <FilterTag
                            active
                            emphasis="secondary"
                            key={index}
                            onClick={() => onProvinceClick(province)}
                            text={
                                provinces.find(
                                    (province2) => province2 === province
                                ) ?? ''
                            }
                        />
                    ))}

                    {state.selectedCategories.map((category, index) => {
                        let categoryName = ''

                        state.categories.some((category2) =>
                            category2.categories.some(({id}) => {
                                if (id === category) {
                                    categoryName = category2.name

                                    return true
                                }

                                return false
                            })
                        )

                        return (
                            <FilterTag
                                active
                                emphasis="secondary"
                                key={index}
                                onClick={() => onCategoryClick(category)}
                                text={categoryName}
                            />
                        )
                    })}
                </ActiveFilters>
            )}
        </>
    )
}
